// React
import { Fragment, useMemo, useState, useEffect } from "react"

// Router
import { Link } from "@/lib/router"
import { Routes } from "@/constants/routes"

// DateTime
import { DateTime } from "@/lib/dates"

// Queries
import { keepPreviousData } from "@/lib/query"
import {
	InterestPaymentType,
	PaymentStateEnum,
	usePaymentsQuery,
} from "@/api/graphql"

// Icons
import { FiHash } from "@/lib/icons"

// Translations
import { useTrans } from "@/i18n"

// Tables
import {
	Table,
	TableBody,
	TableDataCell,
	TableHead,
	TableHeading,
	TableRowCell,
} from "@/components/table-controls/TableItems"
import {
	useReactTable,
	flexRender,
	createColumnHelper,
	getPaginationRowModel,
	getCoreRowModel,
	CellContext,
} from "@/lib/table"
import { TableEmptyState } from "@/components/table-controls/TableEmptyState"

// UI
import { Button } from "@/components/Button"
import { PaginationAsButtons } from "@/components/PaginationAsButtons"
import { CardFooter } from "@/components/Card"

// Constants
import { dateFormat } from "@/constants/constants"

// Shared components
import { StateSelectorOptionType } from "../_components/paymentsFilters.constants"

const columnHelper = createColumnHelper<InterestPaymentType>()

interface PaymentsListProps {
	filter: string | undefined
	sort: string | undefined
	page: number
	pageSize: number
	setPage: (page: number) => void
	setSort: (sort: string) => void
	selectedStateFilter: StateSelectorOptionType
}

/**
 * PaymentsList for Energy Suppliers
 */
export const PaymentsList = ({
	filter,
	sort,
	page,
	pageSize,
	setPage,
	setSort,
	selectedStateFilter,
}: PaymentsListProps) => {
	// Translations
	const t = useTrans(["common", "payments", "project"])

	// State
	const [data, setData] = useState<InterestPaymentType[] | null>([])

	const columns = useMemo(
		() => [
			columnHelper.accessor((data) => data?.project?.name, {
				id: "project_name",
				header: () => (
					<TableHeading
						fieldName="interestperiod__project__name"
						sort={sort}
						setSort={setSort}
					>
						{t("payments:list.project.title")}
					</TableHeading>
				),
				cell: (info: CellContext<InterestPaymentType, string>) => (
					<TableDataCell className="!p-0 xl:max-w-[30%]">
						<span className="block h-full px-6 py-4 font-bold text-gray-700 hover:text-gray-900">
							{info.getValue()}
						</span>
					</TableDataCell>
				),
			}),
			columnHelper.accessor((data) => data?.transaction_id, {
				id: "transaction_id",
				header: () => (
					<TableHeading
						fieldName="interestperiod__transaction_id"
						sort={sort}
						setSort={setSort}
					>
						{t("project:revenue.table.headers.transaction_id")}
					</TableHeading>
				),
				cell: (info: CellContext<InterestPaymentType, string>) => (
					<TableDataCell className="!p-0 xl:max-w-[30%]">
						<span className="flex h-full px-6 py-4">
							<FiHash
								className="mr-1.5 size-5 shrink-0 text-gray-400"
								aria-hidden="true"
							/>
							{info.getValue()}
						</span>
					</TableDataCell>
				),
			}),
			columnHelper.accessor((data) => data, {
				id: "interest_period",
				header: () => (
					<TableHeading
						fieldName="interestperiod__start"
						sort={sort}
						setSort={setSort}
					>
						{t("payments:list.description")}
					</TableHeading>
				),
				cell: (
					info: CellContext<InterestPaymentType, InterestPaymentType>,
				) => (
					<TableDataCell>
						{t("payments:list.interest_period.copy", {
							start: DateTime.fromSQL(
								info.getValue()?.interestperiod?.start,
							).toFormat(dateFormat),
							end: DateTime.fromSQL(
								info.getValue()?.interestperiod?.end,
							).toFormat(dateFormat),
						})}
					</TableDataCell>
				),
			}),
			// Note: No "amount" or "payment" columns for suppliers
			columnHelper.accessor((data) => data, {
				id: "actions",
				header: () => (
					<TableHeading>
						{t("payments:list.actions.title")}
					</TableHeading>
				),
				cell: (
					info: CellContext<InterestPaymentType, InterestPaymentType>,
				) => {
					const link = `${Routes.FinanceInterestPayments}/supplier/${info.getValue().id}`
					return (
						<TableDataCell>
							<Link to={link}>
								<Button
									variant={
										info.getValue().state ===
										PaymentStateEnum.PendingSupplier
											? "secondary"
											: "transparent"
									}
									size="small"
								>
									{info.getValue().state ===
									PaymentStateEnum.PendingSupplier
										? t("payments:list.actions.approve")
										: t("payments:list.actions.view")}
								</Button>
							</Link>
						</TableDataCell>
					)
				},
			}),
			columnHelper.accessor(
				(data: InterestPaymentType) => {
					return DateTime.fromSQL(
						data?.interestperiod?.payment_deadline_date ?? "",
					).toFormat(dateFormat)
				},
				{
					id: "payment_deadline_date",
					header: () => (
						<TableHeading>
							{t("payments:list.payment_deadline_date.title")}
						</TableHeading>
					),
					cell: (info: CellContext<InterestPaymentType, string>) => (
						<TableDataCell>{info.getValue()}</TableDataCell>
					),
				},
			),
		],
		[t, sort, setSort],
	)

	const tableData = useMemo(() => data ?? [], [data])
	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		state: {
			pagination: {
				pageIndex: 0,
				pageSize: 10,
			},
		},
	})

	return (
		<>
			<Table
				className="min-w-[48rem] lg:min-w-0"
				data-testid="table"
				layout="auto"
			>
				{/* table header */}
				<TableHead>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRowCell key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								return (
									<Fragment key={header.id}>
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
									</Fragment>
								)
							})}
						</TableRowCell>
					))}
				</TableHead>
				{/* table body and table cells, plus table footer */}
				<PaymentsView
					table={table}
					setData={setData}
					setPage={setPage}
					filter={filter}
					sort={sort}
					page={page}
					pageSize={pageSize}
					selectedStateFilter={selectedStateFilter}
				/>
			</Table>
			<PaymentsPagination
				setPage={setPage}
				filter={filter}
				sort={sort}
				page={page}
				pageSize={pageSize}
				selectedStateFilter={selectedStateFilter}
			/>
		</>
	)
}

/**
 * PaymentsListLoadingState
 * Loading skeleton for the supplier payments table
 */
function PaymentsListLoadingState({
	pageSize,
	columnCount,
}: {
	pageSize: number
	columnCount: number
}) {
	return (
		<TableBody>
			{Array.from({ length: pageSize }).map((_, index) => (
				<TableRowCell
					withHover={false}
					key={index}
					isOdd={index % 2 === 0}
				>
					{Array.from({ length: columnCount }).map((_, colIndex) => (
						<TableDataCell key={colIndex}>
							<span className="inline-block h-4 w-3/4 animate-pulse rounded bg-gray-200" />
						</TableDataCell>
					))}
				</TableRowCell>
			))}
		</TableBody>
	)
}

interface PaymentsViewProps {
	pageSize: number
	page: number
	sort: string | undefined
	filter: string | undefined
	setPage: (page: number) => void
	table: ReturnType<typeof useReactTable<InterestPaymentType>>
	setData: (data: InterestPaymentType[] | null) => void
	selectedStateFilter: StateSelectorOptionType
}

function usePaymentsQueryData(
	{
		pageSize,
		page,
		sort,
		filter,
		selectedStateFilter,
	}: Pick<
		PaymentsViewProps,
		"pageSize" | "page" | "sort" | "filter" | "selectedStateFilter"
	>,
	options?: { placeholderData: typeof keepPreviousData },
) {
	// Map state filter to supplier-specific states
	const getSupplierStates = () => {
		switch (selectedStateFilter) {
			case "OPEN":
				return ["pending_supplier"]
			case "DONE":
				return ["done"]
			case "ALL":
				return []
			default:
				return []
		}
	}

	return usePaymentsQuery(
		{
			limit: pageSize,
			offset: page * pageSize,
			ordering: sort,
			q: filter,
			state: getSupplierStates(),
		},
		options,
	)
}

function PaymentsPagination({
	page,
	pageSize,
	sort,
	filter,
	setPage,
	selectedStateFilter,
}: Pick<
	PaymentsViewProps,
	"page" | "pageSize" | "filter" | "sort" | "setPage" | "selectedStateFilter"
>) {
	const t = useTrans("payments")
	const { data } = usePaymentsQueryData(
		{
			page,
			pageSize,
			sort,
			filter,
			selectedStateFilter,
		},
		{ placeholderData: keepPreviousData },
	)

	return (
		<div>
			{data?.interest_payments?.results?.length === 0 && (
				<CardFooter>
					<TableEmptyState>
						{t("payments:outstanding_payments.empty_state")}
					</TableEmptyState>
				</CardFooter>
			)}
			{data?.interest_payments?.results?.length !== 0 && (
				<CardFooter>
					<PaginationAsButtons
						countPerPage={pageSize}
						totalCount={data?.interest_payments?.totalCount ?? 0}
						itemType={"common:pagination.item_types.payment"}
						currentPage={page + 1}
						currentItemsAmount={
							data?.interest_payments?.results?.length ?? 0
						}
						onNextPage={() => setPage(page + 1)}
						onPrevPage={() => setPage(Math.max(page - 1, 0))}
						canGoPrev={page > 0}
						analyticsId="payments"
					/>
				</CardFooter>
			)}
		</div>
	)
}

/**
 * PaymentsView
 */
function PaymentsView({
	table,
	pageSize,
	page,
	sort,
	filter,
	setData,
	selectedStateFilter,
}: PaymentsViewProps) {
	const { data, isLoading, isPlaceholderData } = usePaymentsQueryData({
		page,
		pageSize,
		sort,
		filter,
		selectedStateFilter,
	})

	// Update table data when query results change
	useEffect(() => {
		setData(
			(data?.interest_payments?.results as InterestPaymentType[]) ?? [],
		)
	}, [data?.interest_payments?.results, setData])

	// Loading state
	if (isLoading) {
		return (
			<PaymentsListLoadingState
				pageSize={pageSize}
				columnCount={table.getHeaderGroups()[0]?.headers.length ?? 0}
			/>
		)
	}

	// Data state
	return (
		<>
			{/* table body and table cells */}
			<TableBody
				data-testid="tablebody"
				className={isPlaceholderData ? "opacity-50" : ""}
			>
				{table.getRowModel().rows.map((row, index) => {
					return (
						<TableRowCell
							key={row.id}
							isOdd={index % 2 === 0}
							data-testid={`tablerow-${row?.id}`}
						>
							{row._getAllVisibleCells().map((cell) => {
								return (
									<Fragment key={cell.id}>
										{flexRender(
											cell.column.columnDef.cell,
											cell.getContext(),
										)}
									</Fragment>
								)
							})}
						</TableRowCell>
					)
				})}
			</TableBody>
		</>
	)
}
